import DiagnitariesBook from '../admin/DiagnitariesBook';
import React, { useState, useEffect } from 'react';
import {
  Settings, Image, Users, Leaf, Camera, BookOpen,
  Globe, Save, X, Plus, Trash2, Edit3, ChevronDown, ChevronUp,
  CheckCircle, AlertCircle, Eye, EyeOff
} from 'lucide-react';
import CommunityPostManager         from './CommunityPostManager';
import CommunityTimelineEditor      from './CommunityTimelineEditor';
import CommunityTeamManager         from './CommunityTeamManager';
import CommunityAnnouncementManager from './CommunityAnnouncementManager';
import CloudinaryUpload             from './CloudinaryUpload';

interface Props {
  villageId: string;
  villageName: string;
  onBack: () => void;
  onPublish?: () => void;
}

interface Resource {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  category: 'mineral' | 'agricultural' | 'natural';
}

interface GalleryItem {
  id: string;
  url: string;
  caption: string;
  type: 'image' | 'video';
}

interface PortalData {
  published: boolean;
  heroImageUrl: string;
  chiefName: string;
  chiefTitle: string;
  chiefImageUrl: string;
  chiefMessage: string;
  history: string;
  resources: Resource[];
  gallery: GalleryItem[];
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  facebookUrl: string;
  whatsappNumber: string;
}

const EMPTY_PORTAL: PortalData = {
  published: false,
  heroImageUrl: '',
  chiefName: '',
  chiefTitle: 'His Royal Highness',
  chiefImageUrl: '',
  chiefMessage: '',
  history: '',
  resources: [],
  gallery: [],
  contactEmail: '',
  contactPhone: '',
  contactAddress: '',
  facebookUrl: '',
  whatsappNumber: '',
};

const SECTIONS = [
  { id: 'overview',   label: 'Overview & Hero',    icon: Globe },
  { id: 'leadership', label: 'Chief & LOC Team',   icon: Users },
  { id: 'resources',  label: 'Natural Resources',  icon: Leaf },
  { id: 'gallery',    label: 'Gallery & Videos',   icon: Camera },
  { id: 'history',    label: 'History',            icon: BookOpen },
  { id: 'posts',      label: 'News & Posts',       icon: Edit3 },
  { id: 'timeline',   label: 'Timeline',           icon: ChevronDown },
  { id: 'team',       label: 'Team Directory',     icon: Users },
  { id: 'announce',   label: 'Announcements',      icon: AlertCircle },
  { id: 'contact',    label: 'Contact & Social',   icon: Settings },
  { id: 'publish',    label: 'Publish Settings',   icon: Eye },
];

const CommunityAdminPortal: React.FC<Props> = ({ villageId, villageName, onBack, onPublish }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [portal, setPortal]               = useState<PortalData>({ ...EMPTY_PORTAL });
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [error, setError]                 = useState('');

  // Resource form
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [resourceForm, setResourceForm] = useState<Omit<Resource,'id'>>({
    name: '', description: '', imageUrl: '', category: 'agricultural'
  });
  const [editResourceId, setEditResourceId] = useState<string|null>(null);

  // Gallery form
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [galleryForm, setGalleryForm] = useState<Omit<GalleryItem,'id'>>({
    url: '', caption: '', type: 'image'
  });

  // Load portal data
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`/api/community-portals/${villageId}`);
        const d = await r.json();
        if (d.success && d.portal) {
          setPortal({ ...EMPTY_PORTAL, ...d.portal });
        }
      } catch { setError('Failed to load portal data'); }
      finally { setLoading(false); }
    };
    load();
  }, [villageId]);

  const save = async (showFeedback = true) => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const r = await fetch(`/api/community-portals/${villageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(portal),
      });
      const d = await r.json();
      if (d.success) {
        if (showFeedback) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
        if (portal.published && onPublish) onPublish();
      } else setError(d.error || 'Save failed');
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const updatePortal = (updates: Partial<PortalData>) => {
    setPortal(p => ({ ...p, ...updates }));
  };

  // ── Resource handlers ──────────────────────────────────────────────────────
  const addResource = () => {
    if (!resourceForm.name.trim()) return;
    if (editResourceId) {
      updatePortal({
        resources: portal.resources.map(r =>
          r.id === editResourceId ? { ...r, ...resourceForm } : r
        )
      });
      setEditResourceId(null);
    } else {
      const newRes: Resource = {
        id: `res_${Date.now()}`,
        ...resourceForm,
      };
      updatePortal({ resources: [...portal.resources, newRes] });
    }
    setResourceForm({ name: '', description: '', imageUrl: '', category: 'agricultural' });
    setShowResourceForm(false);
  };

  const editResource = (res: Resource) => {
    setEditResourceId(res.id);
    setResourceForm({ name: res.name, description: res.description, imageUrl: res.imageUrl, category: res.category });
    setShowResourceForm(true);
  };

  const deleteResource = (id: string) => {
    if (!confirm('Delete this resource?')) return;
    updatePortal({ resources: portal.resources.filter(r => r.id !== id) });
  };

  // ── Gallery handlers ───────────────────────────────────────────────────────
  const addGalleryItem = () => {
    if (!galleryForm.url.trim()) return;
    const item: GalleryItem = { id: `gal_${Date.now()}`, ...galleryForm };
    updatePortal({ gallery: [...portal.gallery, item] });
    setGalleryForm({ url: '', caption: '', type: 'image' });
    setShowGalleryForm(false);
  };

  const deleteGalleryItem = (id: string) => {
    if (!confirm('Remove this item from gallery?')) return;
    updatePortal({ gallery: portal.gallery.filter(g => g.id !== id) });
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-[#D4AF37] text-lg animate-pulse">Loading portal…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">{villageName}</h1>
              <p className="text-xs text-slate-400">Community Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-green-400 text-sm flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Saved
              </span>
            )}
            {error && <span className="text-red-400 text-xs">{error}</span>}
            <button onClick={() => save()}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                         bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar */}
        <div className="w-56 flex-shrink-0">
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden sticky top-24">
            {SECTIONS.map(sec => {
              const Icon = sec.icon;
              return (
                <button key={sec.id}
                  onClick={() => setActiveSection(sec.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left
                    transition-colors border-b border-slate-800 last:border-0
                    ${activeSection === sec.id
                      ? 'bg-[#0F5132]/30 text-[#D4AF37] font-medium'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{sec.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* ── OVERVIEW ─────────────────────────────────────────────────── */}
          {activeSection === 'overview' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-[#D4AF37]">Overview & Hero Image</h2>

              <CloudinaryUpload
                label="Community Hero / Banner Image"
                currentUrl={portal.heroImageUrl}
                folder={`erue-festival/${villageId}`}
                type="image"
                onUpload={(url) => updatePortal({ heroImageUrl: url })}
              />

              {portal.heroImageUrl && (
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Or paste image URL directly</label>
                  <input value={portal.heroImageUrl}
                    onChange={e => updatePortal({ heroImageUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
              )}
            </div>
          )}

          {/* ── LEADERSHIP ───────────────────────────────────────────────── */}
          {activeSection === 'leadership' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-[#D4AF37]">Chief & LOC Team</h2>

              {/* Chief section */}
              <div className="border border-slate-700 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-white">👑 Community Chief</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Chief's Full Name</label>
                    <input value={portal.chiefName}
                      onChange={e => updatePortal({ chiefName: e.target.value })}
                      placeholder="e.g. Chief John Doe"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Title</label>
                    <input value={portal.chiefTitle}
                      onChange={e => updatePortal({ chiefTitle: e.target.value })}
                      placeholder="e.g. His Royal Highness"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  </div>
                </div>

                <CloudinaryUpload
                  label="Chief's Portrait Photo"
                  currentUrl={portal.chiefImageUrl}
                  folder={`erue-festival/${villageId}/chief`}
                  type="image"
                  onUpload={(url) => updatePortal({ chiefImageUrl: url })}
                />

                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Chief's Message / Welcome Note</label>
                  <textarea rows={4} value={portal.chiefMessage}
                    onChange={e => updatePortal({ chiefMessage: e.target.value })}
                    placeholder="A welcome message from the community chief..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
                </div>
              </div>

              {/* LOC Team */}
              <div className="border border-slate-700 rounded-xl p-4 space-y-4">
                <h3 className="font-medium text-white">🏛️ LOC Team Members</h3>
                <p className="text-xs text-slate-400">Add up to 9 Local Organising Committee members</p>
                <CommunityTeamManager villageId={villageId} villageName={villageName} />
              </div>
            </div>
          )}

          {/* ── NATURAL RESOURCES ────────────────────────────────────────── */}
          {activeSection === 'resources' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#D4AF37]">Natural Resources</h2>
                <button onClick={() => { setShowResourceForm(true); setEditResourceId(null); setResourceForm({ name:'', description:'', imageUrl:'', category:'agricultural'}); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
                  <Plus className="w-4 h-4" /> Add Resource
                </button>
              </div>

              {showResourceForm && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
                  <h4 className="font-semibold text-[#D4AF37]">{editResourceId ? '✏️ Edit Resource' : '➕ New Resource'}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-300 mb-1 block">Name *</label>
                      <input value={resourceForm.name}
                        onChange={e => setResourceForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Palm Oil, Iron Ore, Cassava..."
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                    </div>
                    <div>
                      <label className="text-sm text-slate-300 mb-1 block">Category</label>
                      <select value={resourceForm.category}
                        onChange={e => setResourceForm(f => ({ ...f, category: e.target.value as any }))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                        <option value="agricultural">🌾 Agricultural</option>
                        <option value="mineral">⛏️ Mineral</option>
                        <option value="natural">🌿 Natural/Forest</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-slate-300 mb-1 block">Description</label>
                      <textarea rows={3} value={resourceForm.description}
                        onChange={e => setResourceForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Brief description of this resource and its significance..."
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
                    </div>
                    <div className="md:col-span-2">
                      <CloudinaryUpload
                        label="Resource Photo"
                        currentUrl={resourceForm.imageUrl}
                        folder={`erue-festival/${villageId}/resources`}
                        type="image"
                        onUpload={(url) => setResourceForm(f => ({ ...f, imageUrl: url }))}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={addResource}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] transition-colors">
                      <Save className="w-4 h-4" /> {editResourceId ? 'Update' : 'Add Resource'}
                    </button>
                    <button onClick={() => setShowResourceForm(false)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {portal.resources.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
                  <Leaf className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No resources added yet.</p>
                  <p className="text-slate-500 text-xs mt-1">Add minerals, agricultural products or natural resources.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {portal.resources.map(res => (
                    <div key={res.id}
                      className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition-colors">
                      {res.imageUrl && (
                        <img src={res.imageUrl} alt={res.name}
                          className="w-full h-32 object-cover" />
                      )}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm">{res.name}</p>
                            <p className="text-[#D4AF37] text-xs capitalize">{res.category}</p>
                            {res.description && (
                              <p className="text-slate-400 text-xs mt-1 line-clamp-2">{res.description}</p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => editResource(res)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-700 transition-colors">
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => deleteResource(res.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── GALLERY & VIDEOS ─────────────────────────────────────────── */}
          {activeSection === 'gallery' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#D4AF37]">Gallery & Cultural Videos</h2>
                <button onClick={() => setShowGalleryForm(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
                  <Plus className="w-4 h-4" /> Add Media
                </button>
              </div>

              {showGalleryForm && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
                  <h4 className="font-semibold text-[#D4AF37]">➕ Add Gallery Item</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm text-slate-300 mb-1 block">Media Type</label>
                      <select value={galleryForm.type}
                        onChange={e => setGalleryForm(f => ({ ...f, type: e.target.value as any }))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                        <option value="image">🖼️ Photo / Image</option>
                        <option value="video">🎬 Video</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <CloudinaryUpload
                        label={galleryForm.type === 'video' ? 'Upload Video' : 'Upload Photo'}
                        currentUrl={galleryForm.url}
                        folder={`erue-festival/${villageId}/gallery`}
                        type={galleryForm.type}
                        maxSizeMb={galleryForm.type === 'video' ? 100 : 10}
                        onUpload={(url) => setGalleryForm(f => ({ ...f, url }))}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm text-slate-300 mb-1 block">Caption</label>
                      <input value={galleryForm.caption}
                        onChange={e => setGalleryForm(f => ({ ...f, caption: e.target.value }))}
                        placeholder="Brief caption for this media..."
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={addGalleryItem}
                      disabled={!galleryForm.url}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
                      <Plus className="w-4 h-4" /> Add to Gallery
                    </button>
                    <button onClick={() => setShowGalleryForm(false)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {portal.gallery.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
                  <Camera className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No gallery items yet.</p>
                  <p className="text-slate-500 text-xs mt-1">Add festival photos, cultural images and videos.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {portal.gallery.map(item => (
                    <div key={item.id} className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-800">
                      {item.type === 'video' ? (
                        <video src={item.url} className="w-full h-32 object-cover" />
                      ) : (
                        <img src={item.url} alt={item.caption} className="w-full h-32 object-cover" />
                      )}
                      <div className="p-2">
                        <p className="text-xs text-slate-400 truncate">{item.caption || 'No caption'}</p>
                        <p className="text-xs text-slate-600">{item.type}</p>
                      </div>
                      <button
                        onClick={() => deleteGalleryItem(item.id)}
                        className="absolute top-2 right-2 bg-red-900/80 rounded-full p-1
                                   opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3 h-3 text-red-300" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── HISTORY ──────────────────────────────────────────────────── */}
          {activeSection === 'history' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-[#D4AF37]">Community History</h2>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">
                  Short History of {villageName} and Its People
                </label>
                <textarea
                  rows={12}
                  value={portal.history}
                  onChange={e => updatePortal({ history: e.target.value })}
                  placeholder={`Write the history of ${villageName} here — founding story, notable events, cultural heritage, relationship with Uzairue Kingdom...`}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2
                             text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y
                             leading-relaxed"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {portal.history.length} characters · ~{Math.ceil(portal.history.split(' ').filter(Boolean).length / 200)} min read
                </p>
              </div>
              <div className="border-t border-slate-700 pt-4">
                <h3 className="font-medium text-white mb-3">📅 Historical Timeline Events</h3>
                <p className="text-xs text-slate-400 mb-4">Add specific dated events to the visual timeline.</p>
                <CommunityTimelineEditor villageId={villageId} villageName={villageName} />
              </div>
            </div>
          )}

          {/* ── POSTS ────────────────────────────────────────────────────── */}
          {activeSection === 'posts' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
              <CommunityPostManager villageId={villageId} villageName={villageName} />
            </div>
          )}

          {/* ── TIMELINE ─────────────────────────────────────────────────── */}
          {activeSection === 'timeline' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
              <CommunityTimelineEditor villageId={villageId} villageName={villageName} />
            </div>
          )}

          {/* ── TEAM ─────────────────────────────────────────────────────── */}
          {activeSection === 'team' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
              <CommunityTeamManager villageId={villageId} villageName={villageName} />
            </div>
          )}

          {/* ── ANNOUNCEMENTS ────────────────────────────────────────────── */}
          {activeSection === 'announce' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
              <CommunityAnnouncementManager villageId={villageId} villageName={villageName} />
            </div>
          )}

          {/* ── CONTACT & SOCIAL ─────────────────────────────────────────── */}
          {activeSection === 'contact' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-[#D4AF37]">Contact & Social Media</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Contact Email</label>
                  <input value={portal.contactEmail}
                    onChange={e => updatePortal({ contactEmail: e.target.value })}
                    placeholder="community@uzairue.org"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Contact Phone</label>
                  <input value={portal.contactPhone}
                    onChange={e => updatePortal({ contactPhone: e.target.value })}
                    placeholder="+234..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-slate-300 mb-1 block">Community Address</label>
                  <input value={portal.contactAddress}
                    onChange={e => updatePortal({ contactAddress: e.target.value })}
                    placeholder="e.g. Jattu-Uzairue, Etsako West, Edo State"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Facebook Page URL</label>
                  <input value={portal.facebookUrl}
                    onChange={e => updatePortal({ facebookUrl: e.target.value })}
                    placeholder="https://facebook.com/..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">WhatsApp Number</label>
                  <input value={portal.whatsappNumber}
                    onChange={e => updatePortal({ whatsappNumber: e.target.value })}
                    placeholder="+234..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
            </div>
          )}

          {/* ── PUBLISH SETTINGS ─────────────────────────────────────────── */}
          
          {activeSection === 'dignitaries' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
              <DiagnitariesBook
                isAdmin={true}
                currentUser={undefined}
                communityFilter={villageId}
              />
            </div>
          )}

          {activeSection === 'publish' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-[#D4AF37]">Publish Settings</h2>

              <div className={`border rounded-xl p-6 text-center space-y-4
                ${portal.published ? 'border-green-700 bg-green-900/10' : 'border-slate-700'}`}>
                <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center
                  ${portal.published ? 'bg-green-900/30' : 'bg-slate-800'}`}>
                  {portal.published
                    ? <Globe className="w-8 h-8 text-green-400" />
                    : <EyeOff className="w-8 h-8 text-slate-400" />
                  }
                </div>
                <div>
                  <p className="text-lg font-semibold text-white">
                    {portal.published ? '🌐 Portal is LIVE' : '🔒 Portal is Private'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {portal.published
                      ? `${villageName} portal is visible to the public.`
                      : 'Enable publishing to make this portal public.'}
                  </p>
                </div>
                <button
                  onClick={() => { updatePortal({ published: !portal.published }); }}
                  className={`px-8 py-3 rounded-xl font-semibold text-sm transition-colors
                    ${portal.published
                      ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      : 'bg-[#0F5132] text-white hover:bg-[#0d4429]'}`}
                >
                  {portal.published ? 'Unpublish Portal' : 'Publish Portal'}
                </button>
              </div>

              <div className="bg-slate-800/40 rounded-xl p-4 space-y-2">
                <h3 className="text-sm font-medium text-white">Portal Checklist</h3>
                {[
                  { label: 'Hero image',       done: !!portal.heroImageUrl },
                  { label: "Chief's name",     done: !!portal.chiefName },
                  { label: "Chief's photo",    done: !!portal.chiefImageUrl },
                  { label: 'Community history',done: portal.history.length > 50 },
                  { label: 'Resources added',  done: portal.resources.length > 0 },
                  { label: 'Gallery items',    done: portal.gallery.length > 0 },
                  { label: 'Contact info',     done: !!portal.contactEmail || !!portal.contactPhone },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full flex-shrink-0
                      ${item.done ? 'bg-green-500' : 'bg-slate-600'}`}>
                    </div>
                    <span className={`text-sm ${item.done ? 'text-green-400' : 'text-slate-500'}`}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>

              <button onClick={() => save()}
                disabled={saving}
                className="w-full py-3 rounded-xl font-semibold text-sm
                           bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f]
                           disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : '💾 Save & Apply Settings'}
              </button>
            </div>
          )}

          {/* Auto-save notice */}
          <div className="text-center">
            <p className="text-xs text-slate-600">
              Changes are not auto-saved — click <strong className="text-slate-500">Save All</strong> in the header to save.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CommunityAdminPortal;
